import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
} from "react-router-dom";

import {
  DataGrid,
} from "@mui/x-data-grid";

import {
  ThemeProvider,
  createTheme,
} from "@mui/material";

import {
  motion,
} from "framer-motion";

import {
  FiEdit3,
  FiGrid,
  FiImage,
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiFolder,
} from "react-icons/fi";

import {
  MdOutlineCategory,
} from "react-icons/md";

import {
  Loader,
} from "../../components/utils";

import {
  getFillterCategorys,
} from "../../redux/actions/category";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  media,
} from "../../server";

import {
  truncateText,
} from "../hooks/TextVeiw";

import {
  localeTextGrid,
} from "../../constants";

import CategoryFilter
  from "./utils/CategoryFilter";


function DAllCategories() {
  const {
    t,
  } = useTranslation();

  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const {
    currentDir,
  } = useStateContext();


  /* =====================================
      REDUX
  ====================================== */

  const {
    isLoading,
    Filltercategorys,
    pagination,
  } = useSelector(
    (state) =>
      state.Categorys
  );


  /* =====================================
      SEARCH
  ====================================== */

  const [
    titleAr,
    setTitleAr,
  ] = useState("");


  const [
    titleEn,
    setTitleEn,
  ] = useState("");


  const [
    debouncedTitleAr,
    setDebouncedTitleAr,
  ] = useState("");


  const [
    debouncedTitleEn,
    setDebouncedTitleEn,
  ] = useState("");


  /* =====================================
      PAGINATION
  ====================================== */

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 10,
  });


  const page =
    paginationModel.page;

  const limit =
    paginationModel.pageSize;


  const categories =
    Array.isArray(
      Filltercategorys
    )
      ? Filltercategorys
      : [];


  const totalItems =
    pagination?.totalItems ||
    0;


  /* =====================================
      DEBOUNCE
  ====================================== */

  useEffect(() => {

    const timer =
      setTimeout(() => {

        setDebouncedTitleAr(
          titleAr.trim()
        );

        setDebouncedTitleEn(
          titleEn.trim()
        );

      }, 450);


    return () =>
      clearTimeout(timer);

  }, [
    titleAr,
    titleEn,
  ]);


  useEffect(() => {

    setPaginationModel(
      (prev) => ({
        ...prev,
        page: 0,
      })
    );

  }, [
    debouncedTitleAr,
    debouncedTitleEn,
  ]);


  /* =====================================
      GET CATEGORIES
  ====================================== */

  useEffect(() => {

    dispatch(
      getFillterCategorys(
        page,
        limit,
        debouncedTitleAr,
        debouncedTitleEn
      )
    );

  }, [
    page,
    limit,
    debouncedTitleAr,
    debouncedTitleEn,
    dispatch,
  ]);


  /* =====================================
      RESET SEARCH
  ====================================== */

  const resetFilters = () => {

    setTitleAr("");
    setTitleEn("");

    setDebouncedTitleAr("");
    setDebouncedTitleEn("");

    setPaginationModel(
      (prev) => ({
        ...prev,
        page: 0,
      })
    );

  };


  /* =====================================
      EDIT
  ====================================== */

  const updateCategoryHandler =
    (id) => {

      navigate(
        `/EditCategoryPage/${id}`
      );

    };


  /* =====================================
      MUI THEME
  ====================================== */

  const theme =
    useMemo(
      () =>
        createTheme({
          direction:
            currentDir,

          typography: {
            fontFamily:
              "inherit",
          },
        }),

      [
        currentDir,
      ]
    );


  /* =====================================
      COLUMNS
  ====================================== */

  const columns =
    useMemo(
      () => [

        /* =============================
            IMAGE
        ============================== */

        {
          field: "Image",

          headerName:
            t("Image"),

          width: 95,

          minWidth: 95,

          sortable:
            false,

          filterable:
            false,

          align: "center",

          headerAlign:
            "center",

          renderCell: (
            params
          ) => {

            return (
              <div
                className="
                  flex
                  w-full
                  h-full

                  items-center
                  justify-center
                "
              >

                {params.value ? (

                  <img
                    src={`${media}${params.value}`}

                    alt={
                      params.row
                        ?.titleAr ||
                      "Category"
                    }

                    loading="lazy"

                    className="
                      w-[54px]
                      h-[54px]

                      rounded-[16px]

                      object-cover

                      border
                      border-color-1/15

                      shadow-sm
                    "
                  />

                ) : (

                  <div
                    className="
                      flex

                      w-[54px]
                      h-[54px]

                      items-center
                      justify-center

                      rounded-[16px]

                      bg-color-1/10

                      text-color-1
                    "
                  >
                    <FiImage
                      size={18}
                    />
                  </div>

                )}

              </div>
            );
          },
        },


        /* =============================
            ARABIC TITLE
        ============================== */

        {
          field:
            "titleAr",

          headerName:
            t(
              "Title (Ar)"
            ),

          minWidth: 170,

          flex: 0.8,

          filterable:
            false,

          sortable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="rtl"

              className="
                flex

                w-full
                h-full

                items-center
              "
            >

              <span
                className="
                  block

                  w-full

                  overflow-hidden
                  text-ellipsis
                  whitespace-nowrap

                  font-kufi
                  font-semibold

                  text-[13px]

                  leading-normal

                  text-color-2
                  dark:text-white
                "
                title={
                  params.value ||
                  ""
                }
              >
                {
                  params.value ||
                  "-"
                }
              </span>

            </div>

          ),
        },


        /* =============================
            ENGLISH TITLE
        ============================== */

        {
          field:
            "titleEn",

          headerName:
            t(
              "Title (En)"
            ),

          minWidth: 170,

          flex: 0.8,

          filterable:
            false,

          sortable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="ltr"

              className="
                flex

                w-full
                h-full

                items-center

                text-start
              "
            >

              <span
                className="
                  block

                  w-full

                  overflow-hidden
                  text-ellipsis
                  whitespace-nowrap

                  font-medium

                  text-[13px]

                  leading-normal

                  text-color-2
                  dark:text-white/90
                "
                title={
                  params.value ||
                  ""
                }
              >
                {
                  params.value ||
                  "-"
                }
              </span>

            </div>

          ),
        },


        /* =============================
            ARABIC DESCRIPTION
        ============================== */

        {
          field:
            "textAr",

          headerName:
            t(
              "Text (Ar)"
            ),

          minWidth: 230,

          flex: 1.25,

          filterable:
            false,

          sortable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="rtl"

              className="
                flex

                w-full
                h-full

                items-center
              "
            >

              <p
                className="
                  w-full

                  overflow-hidden

                  font-kufi

                  text-[11px]

                  leading-6

                  text-color-2/65
                  dark:text-white/55
                "
              >
                {truncateText(
                  params.value ||
                    "",
                  8
                )}
              </p>

            </div>

          ),
        },


        /* =============================
            ENGLISH DESCRIPTION
        ============================== */

        {
          field:
            "textEn",

          headerName:
            t(
              "Text (En)"
            ),

          minWidth: 230,

          flex: 1.25,

          filterable:
            false,

          sortable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="ltr"

              className="
                flex

                w-full
                h-full

                items-center

                text-start
              "
            >

              <p
                className="
                  w-full

                  overflow-hidden

                  text-[11px]

                  leading-6

                  text-color-2/65
                  dark:text-white/55
                "
              >
                {truncateText(
                  params.value ||
                    "",
                  8
                )}
              </p>

            </div>

          ),
        },


        /* =============================
            ACTIONS
        ============================== */

        {
          field: "actions",
          headerName: t("Actions"),
          width: 130,
          minWidth: 130,
          sortable: false,
          filterable: false,
          align: "center",
          headerAlign: "center",
          renderCell: (params) => (
            <div className="w-full h-full flex items-center justify-center">
              <button
                type="button"
                onClick={() => updateCategoryHandler(params.row._id)}
                className="
                  inline-flex items-center justify-center gap-2
                  h-10 min-w-[92px] px-4
                  rounded-2xl
                  border border-color-1/20
                  bg-color-1/10
                  text-color-1
                  font-kufi text-sm
                  whitespace-nowrap
                  transition-all duration-300
                  hover:bg-color-1 hover:text-white hover:border-color-1
                "
              >
                <FiEdit3 size={14} />
                <span>{t("Edit")}</span>
              </button>
            </div>
          ),
        }

      ],

      [
        t,
      ]
    );


  /* =====================================
      LOADER
  ====================================== */

  if (
    isLoading &&
    categories.length === 0
  ) {
    return <Loader />;
  }


  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 15,
      }}

      animate={{
        opacity: 1,
        y: 0,
      }}

      className="
        w-full

        max-w-[1600px]

        mx-auto
      "
    >

      {/* =====================================
          PAGE HERO
      ====================================== */}

      <section
        className="
          relative

          overflow-hidden

          rounded-[2rem]

          bg-color-2

          px-5
          py-6

          md:px-8
          md:py-8

          mb-6

          text-white

          shadow-[0_18px_50px_rgba(68,37,20,.13)]
        "
      >

        <div
          className="
            absolute

            -top-28
            -end-20

            w-72
            h-72

            rounded-full

            bg-color-1/20

            blur-[100px]

            pointer-events-none
          "
        />


        <div
          className="
            relative
            z-10

            flex
            flex-col

            md:flex-row
            md:items-center
            md:justify-between

            gap-5
          "
        >

          <div
            className="
              flex
              items-center

              gap-4
            "
          >

            <div
              className="
                flex

                w-14
                h-14

                shrink-0

                items-center
                justify-center

                rounded-2xl

                bg-color-1

                text-2xl

                shadow-lg
              "
            >
              <MdOutlineCategory />
            </div>


            <div>

              <span
                className="
                  font-kufi

                  text-[10px]

                  text-color-1
                "
              >
                {t(
                  "Categories Management"
                )}
              </span>


              <h1
                className="
                  mt-1

                  font-kufi
                  font-bold

                  text-xl
                  md:text-3xl
                "
              >
                {t(
                  "Meal Categories"
                )}
              </h1>


              <p
                className="
                  mt-2

                  max-w-xl

                  font-kufi

                  text-[10px]
                  md:text-xs

                  leading-6

                  text-white/50
                "
              >
                {t(
                  "Manage and organize Yaman restaurant meal categories"
                )}
              </p>

            </div>

          </div>


          <button
            type="button"

            onClick={() =>
              navigate(
                "/CreateCategoryPage"
              )
            }

            className="
              flex

              min-h-[44px]

              items-center
              justify-center

              gap-2

              rounded-full

              bg-color-1

              px-5

              font-kufi
              text-xs

              transition-all

              hover:bg-white
              hover:text-color-2
            "
          >
            <FiPlus />

            {t(
              "Create Category"
            )}
          </button>

        </div>

      </section>


      {/* =====================================
          FILTER + COUNT
      ====================================== */}

      <section
        className="
          grid

          grid-cols-1

          xl:grid-cols-[220px_1fr]

          gap-4

          mb-5
        "
      >

        {/* Count */}

        <div
          className="
            relative

            overflow-hidden

            rounded-[1.5rem]

            border
            border-color-2/10
            dark:border-white/10

            bg-back-primery
            dark:bg-back-darksecondary

            p-5

            shadow-[0_8px_25px_rgba(68,37,20,.04)]
          "
        >

          <div
            className="
              flex
              items-center
              justify-between
            "
          >

            <div>

              <div
                className="
                  text-3xl
                  font-bold

                  text-color-2
                  dark:text-white
                "
              >
                {totalItems}
              </div>


              <p
                className="
                  mt-1

                  font-kufi
                  text-[11px]

                  text-color-2/65
                  dark:text-white/60
                "
              >
                {t(
                  "Total Categories"
                )}
              </p>

            </div>


            <div
              className="
                flex

                w-11
                h-11

                items-center
                justify-center

                rounded-2xl

                bg-color-1/10

                text-color-1
              "
            >
              <FiFolder />
            </div>

          </div>

        </div>


        {/* Search */}

        <div
          className="
            rounded-[1.5rem]

            border
            border-color-2/10
            dark:border-white/10

            bg-back-primery
            dark:bg-back-darksecondary

            p-4
            md:p-5

            shadow-[0_8px_25px_rgba(68,37,20,.04)]
          "
        >

          <div
            className="
              flex

              items-center
              justify-between

              gap-3

              mb-4
            "
          >

            <div
              className="
                flex

                items-center

                gap-3
              "
            >

              <div
                className="
                  flex

                  w-9
                  h-9

                  items-center
                  justify-center

                  rounded-xl

                  bg-color-1/10

                  text-color-1
                "
              >
                <FiSearch />
              </div>


              <span
                className="
                  font-kufi
                  font-semibold

                  text-xs

                  text-color-2
                  dark:text-white
                "
              >
                {t(
                  "Search Categories"
                )}
              </span>

            </div>


            {(titleAr ||
              titleEn) && (

              <button
                type="button"

                onClick={
                  resetFilters
                }

                className="
                  flex

                  items-center

                  gap-2

                  rounded-full

                  bg-color-1/10

                  px-3
                  py-2

                  font-kufi
                  text-[10px]

                  text-color-1
                "
              >
                <FiRefreshCw />

                {t(
                  "Reset Filters"
                )}
              </button>

            )}

          </div>


          <CategoryFilter
            titleAr={
              titleAr
            }

            setTitleAr={
              setTitleAr
            }

            titleEn={
              titleEn
            }

            setTitleEn={
              setTitleEn
            }
          />

        </div>

      </section>


      {/* =====================================
          TABLE
      ====================================== */}

      <section
        className="
          overflow-hidden

          rounded-[1.75rem]

          border
          border-color-2/10
          dark:border-white/10

          bg-back-primery
          dark:bg-back-darksecondary

          shadow-[0_12px_35px_rgba(68,37,20,.05)]
        "
      >

        {/* Header */}

        <div
          className="
            flex

            items-center

            gap-3

            px-5
            md:px-6

            py-5

            border-b
            border-color-2/10
            dark:border-white/10
          "
        >

          <div
            className="
              flex

              w-10
              h-10

              items-center
              justify-center

              rounded-xl

              bg-color-1/10

              text-color-1
            "
          >
            <FiGrid />
          </div>


          <div>

            <h2
              className="
                font-kufi
                font-bold

                text-sm

                text-color-2
                dark:text-white
              "
            >
              {t(
                "Categories List"
              )}
            </h2>


            <p
              className="
                mt-1

                font-kufi
                text-[9px]

                text-color-2/45
                dark:text-white/35
              "
            >
              {t(
                "View and edit all restaurant meal categories"
              )}
            </p>

          </div>

        </div>


        {/* Grid */}

        <ThemeProvider
          theme={theme}
        >

          <div
            className="
              w-full

              overflow-x-auto

              p-3
              md:p-5
            "
          >

            <DataGrid
              rows={
                categories
              }

              columns={
                columns
              }

              getRowId={(
                row
              ) =>
                row._id
              }

              disableRowSelectionOnClick

              disableColumnSelector

              pagination

              paginationMode="server"

              rowCount={
                totalItems
              }

              paginationModel={
                paginationModel
              }

              onPaginationModelChange={
                setPaginationModel
              }

              pageSizeOptions={[
                10,
                20,
                50,
              ]}

              localeText={
                localeTextGrid
              }

              loading={
                isLoading
              }

              /* مهم جدًا */

              rowHeight={
                82
              }

              columnHeaderHeight={
                58
              }

              sx={{

                width:
                  "100%",

                minWidth:
                  "1050px",

                border:
                  "none",

                backgroundColor:
                  "transparent",


                /* =============================
                    HEADERS
                ============================== */

                "& .MuiDataGrid-columnHeaders":
                  {
                    backgroundColor:
                      "#fff",

                    borderBottom:
                      "1px solid rgba(68,37,20,.09)",
                  },


                "& .MuiDataGrid-columnHeader":
                  {
                    paddingLeft:
                      "16px",

                    paddingRight:
                      "16px",

                    outline:
                      "none !important",
                  },


                "& .MuiDataGrid-columnHeaderTitleContainer":
                  {
                    height:
                      "100%",

                    display:
                      "flex",

                    alignItems:
                      "center",
                  },


                "& .MuiDataGrid-columnHeaderTitle":
                  {
                    fontSize:
                      "11px",

                    fontWeight:
                      "700",

                    overflow:
                      "visible",

                    lineHeight:
                      "1.4",

                    whiteSpace:
                      "nowrap",
                  },


                /* =============================
                    CELLS
                ============================== */

                "& .MuiDataGrid-cell":
                  {
                    paddingLeft:
                      "16px",

                    paddingRight:
                      "16px",

                    borderBottom:
                      "1px solid rgba(68,37,20,.06)",

                    outline:
                      "none !important",

                    overflow:
                      "hidden",
                  },


                "& .MuiDataGrid-cellContent":
                  {
                    width:
                      "100%",

                    overflow:
                      "hidden",
                  },


                /* =============================
                    ROWS
                ============================== */

                "& .MuiDataGrid-row":
                  {
                    backgroundColor:
                      "transparent",

                    transition:
                      "background-color .2s ease",
                  },


                "& .MuiDataGrid-row:hover":
                  {
                    backgroundColor:
                      "rgba(165,119,58,.045)",
                  },


                "& .MuiDataGrid-row:last-child .MuiDataGrid-cell":
                  {
                    borderBottom:
                      "none",
                  },


                /* =============================
                    FOOTER
                ============================== */

                "& .MuiDataGrid-footerContainer":
                  {
                    minHeight:
                      "58px",

                    borderTop:
                      "1px solid rgba(68,37,20,.08)",
                  },


                /* =============================
                    SCROLLBAR
                ============================== */

                "& .MuiDataGrid-scrollbar::-webkit-scrollbar":
                  {
                    height:
                      "6px",

                    width:
                      "6px",
                  },


                "& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb":
                  {
                    backgroundColor:
                      "rgba(165,119,58,.35)",

                    borderRadius:
                      "20px",
                  },


                /* =============================
                    REMOVE FOCUS
                ============================== */

                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline:
                      "none",
                  },
              }}
            />

          </div>

        </ThemeProvider>

      </section>

    </motion.div>
  );
}


export default DAllCategories;